import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, StatusPill, BRANCH_LABEL, SortTh, useLocalSort, BIN_ROLES } from "../components/common";
import { Staff } from "../components/StaffAvatar";

const dt = (s) => new Date(s).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" });
const daysLeft = (s) => Math.max(0, Math.ceil((new Date(s) - new Date()) / 86400000));
const KIND = {
  leads: { url: "/leads-bin", id: "lead_id", head: [["lead_id", "Lead ID"], ["company_name", "Company"], ["owner", "Owner"], ["branch", "Branch"], ["status", "Status"]] },
  uids: { url: "/orders-bin", id: "uid", head: [["uid", "UID"], ["client_name", "Client"], ["owner", "Account owner"], ["branch", "Branch"], ["record_class", "Type / lead"]] },
};

const Cells = ({ kind, r }) => (kind === "leads" ? (
  <><td>{r.company_name || r.contact_name || "—"}</td><td><Staff name={r.owner} size={20} /></td><td className="text-xs">{BRANCH_LABEL[r.branch] || r.branch}</td><td><StatusPill code={r.status} /></td></>
) : (
  <><td>{r.client_name || "—"}</td><td><Staff name={r.owner} size={20} /></td><td className="text-xs">{BRANCH_LABEL[r.branch] || r.branch}</td>
    <td className="text-xs">{(r.record_class || "").replace("_", "-")}{(r.leads || []).map((l) => <Link key={l} to={`/sales/leads/${l}`} className="block font-mono text-primary">{l}</Link>)}</td></>
));

const Card = ({ kind, r, id, onRestore }) => (
  <div className="ax-panel p-4" data-testid={`bin-card-${id}`}>
    <div className="flex justify-between items-start gap-3">
      <div className="min-w-0">
        <div className="font-mono text-xs text-primary">{id}{kind === "uids" && r.record_class ? ` · ${r.record_class.replace("_", "-")}` : ""}</div>
        <div className="font-medium truncate">{(kind === "leads" ? r.company_name || r.contact_name : r.client_name) || "—"}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{r.owner || "No owner"} · {BRANCH_LABEL[r.branch] || r.branch}</div>
      </div>
      <span className="text-xs whitespace-nowrap border rounded-full px-2 py-0.5" data-testid={`bin-card-days-${id}`}>{daysLeft(r.purge_after)} days left</span>
    </div>
    <div className="text-xs mt-2">Deleted {dt(r.deleted_at)} by {r.deleted_by_name}</div>
    {r.reason && <div className="text-xs text-muted-foreground mt-1 break-words">Reason: {r.reason}</div>}
    {kind === "uids" && (r.leads || []).length > 0 && <div className="text-xs mt-1">Lead: {r.leads.map((l) => <Link key={l} to={`/sales/leads/${l}`} className="font-mono text-primary mr-2">{l}</Link>)}</div>}
    <button data-testid={`bin-card-restore-${id}`} onClick={() => onRestore(id)} className="mt-3 w-full h-10 border border-primary text-primary text-sm flex items-center justify-center gap-2 active:bg-primary/10"><RotateCcw className="h-4 w-4" />Restore</button>
  </div>
);

function Bin() {
  const [kind, setKind] = useState("leads");
  const [rows, setRows] = useState([]);
  const k = KIND[kind];
  const cur = useRef(kind);
  cur.current = kind;
  const load = useCallback(() => api.get(KIND[kind].url).then((r) => { if (cur.current === kind) setRows(r.data); }).catch((e) => toast.error(errMsg(e))), [kind]);
  useEffect(() => { setRows([]); load(); }, [load]);
  const restore = (id) => api.post(`${k.url}/${id}/restore`).then(() => { toast.success(`${id} restored`); load(); }).catch((e) => toast.error(errMsg(e)));
  const [sorted, sort] = useLocalSort(rows, "deleted_at", -1);
  return (
    <div>
      <PageHeader eyebrow="Sales & Orders" title="Deleted bin" desc="Deleted leads and UIDs stay here for 30 days, hidden from every list, report and callback. Restore brings them back exactly as they were, including the link between a UID and its lead. After 30 days they're removed for good." />
      <div className="flex gap-2 mb-3">{[["leads", "Leads"], ["uids", "UIDs"]].map(([v, l]) => (
        <button key={v} data-testid={`bin-tab-${v}`} onClick={() => { setRows([]); setKind(v); }} className={`h-9 px-4 text-sm border rounded-full ${kind === v ? "bg-primary text-primary-foreground" : "hover:border-primary"}`}>{l}</button>))}</div>
      <div className="md:hidden space-y-3" data-testid="bin-cards">
        {rows.map((r) => <Card key={r[k.id]} kind={kind} r={r} id={r[k.id]} onRestore={restore} />)}
        {!rows.length && <div className="ax-panel p-6 text-sm text-muted-foreground text-center" data-testid="bin-cards-empty">Nothing in the bin.</div>}
      </div>
      <div className="ax-panel ax-scroll ax-freeze hidden md:block"><table className="ax-table w-full min-w-[900px]" data-testid="leads-bin-table">
        <thead><tr>{[...k.head, ["deleted_at", "Deleted"], [null, "Reason"], ["purge_after", "Days left"]].map(([sk, h]) => <SortTh key={h} k={sk} label={h} sort={sort} />)}<th /></tr></thead>
        <tbody>{sorted.map((r) => (
          <tr key={r[k.id]} data-testid={`bin-row-${r[k.id]}`}>
            <td className="font-mono text-xs">{r[k.id]}</td><Cells kind={kind} r={r} />
            <td className="text-xs">{dt(r.deleted_at)}<div className="text-muted-foreground">by {r.deleted_by_name}</div></td>
            <td className="text-xs max-w-[200px]">{r.reason || "—"}</td><td className="num text-xs">{daysLeft(r.purge_after)}</td>
            <td><button data-testid={`bin-restore-${r[k.id]}`} onClick={() => restore(r[k.id])} className="border text-xs px-2 py-1 flex items-center gap-1 hover:border-primary"><RotateCcw className="h-3 w-3" />Restore</button></td>
          </tr>))}
          {!rows.length && <tr><td colSpan={9} className="text-sm text-muted-foreground py-8 text-center" data-testid="leads-bin-empty">Nothing in the bin.</td></tr>}</tbody>
      </table></div>
    </div>
  );
}

export default function LeadsBin() {
  const { user } = useAuth();
  if (!BIN_ROLES.includes(user?.role_key)) return <div className="ax-panel p-6 text-sm" data-testid="leads-bin-locked">Only founders, branch heads and sales heads can open the Deleted bin.</div>;
  return <Bin />;
}
